import React from 'react';
import {
  Box, Typography, TextField, Button, Select, MenuItem, Divider,
  InputAdornment, CircularProgress, Tooltip,
} from '@mui/material';
import {
  DoneAll as DoneAllIcon, Delete as DeleteIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';

/* ─────────────────────────────────────────────────────────────
   ApplyToManyBar

   One shared "apply to many" toolbar used by every tariff admin
   panel (Services, Diagnosis, Laboratory, Procedure, Scan).

   Type the values once at the top, tick the schemes/insurances in
   the table below, then:
     • "Apply to N"  → writes only the filled fields to every ticked row
     • "Delete N"    → removes every ticked row

   Any field left blank is kept as-is on the ticked rows, so a single
   column can be re-priced without touching the rest.

   props
     entity        singular noun for a ticked row ("scheme")
     ticked/total  ticked row count / total row count
     fields        [{ key, label, placeholder, type, options, width }]
                   type: 'money' | 'number' | 'text' | 'select'
     values        { [key]: string }   controlled field values
     onChange      (key, value) => void
     onApply       () => void          bulk update
     onDelete      () => void          bulk delete
     applying / deleting               busy flags
     onSelectAll / onClearSelection    optional selection helpers
     copy          optional { label, hint, onClick, disabled }
                   renders the "copy one row to other schemes" strip
───────────────────────────────────────────────────────────── */

const labelSx = {
  display: 'block',
  fontSize: '0.66rem',
  fontWeight: 600,
  letterSpacing: 0.3,
  color: '#8695b7',
  mb: 0.35,
};

const fieldSx = {
  background: '#fff',
  borderRadius: 1.5,
  '& .MuiOutlinedInput-root': {
    fontSize: '0.78rem',
    borderRadius: 1.5,
    background: '#fff',
    '& input': { py: '7px' },
    '& fieldset': { borderColor: '#dbe3f5' },
    '&:hover fieldset': { borderColor: '#11998e' },
    '&.Mui-focused fieldset': { borderColor: '#11998e' },
  },
};

const selectSx = {
  fontSize: '0.78rem',
  background: '#fff',
  borderRadius: 1.5,
  '& .MuiSelect-select': { py: '7px' },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#dbe3f5' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#11998e' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#11998e' },
};

const ApplyToManyBar = ({
  entity = 'scheme',
  ticked = 0,
  total = 0,
  fields = [],
  values = {},
  onChange,
  onApply,
  onDelete,
  applying = false,
  deleting = false,
  onSelectAll,
  onClearSelection,
  copy = null,
}) => {
  const isFilled = (key) => {
    const v = values[key];
    return v !== undefined && v !== null && String(v).trim() !== '';
  };
  const filledCount = fields.filter(f => isFilled(f.key)).length;
  const hasValues   = filledCount > 0;
  const busy        = applying || deleting;

  const canApply  = ticked > 0 && hasValues && !busy;
  const canDelete = ticked > 0 && !busy;

  const plural = ticked === 1 ? entity : `${entity}s`;

  const hint = ticked === 0
    ? `Tick the ${entity}s below to edit or delete them together`
    : !hasValues
      ? 'Fill at least one field above'
      : `${filledCount} field${filledCount !== 1 ? 's' : ''} will be written to ${ticked} ${plural} — blank fields stay unchanged`;

  return (
    <Box sx={{
      px: 2.5, py: 1.5, flexShrink: 0,
      background: '#eef2fb',
      borderBottom: '1px solid #dbe3f5',
    }}>
      {/* ── title + ticked counter ─────────────────────────── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
        <Typography sx={{
          fontSize: '0.66rem', fontWeight: 800, letterSpacing: 1.1,
          color: '#8695b7', textTransform: 'uppercase',
        }}>
          Apply to many
        </Typography>
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: ticked > 0 ? '#3949ab' : '#9aa7c2' }}>
          {ticked} of {total} {plural} ticked
        </Typography>

        <Box sx={{ ml: 'auto', display: 'flex', gap: 0.5 }}>
          {onSelectAll && (
            <Button size="small" variant="text" onClick={onSelectAll} disabled={busy || total === 0}
              sx={{ fontSize: '0.68rem', py: 0.1, minWidth: 0, color: '#11998e', textTransform: 'none' }}>
              Tick all
            </Button>
          )}
          {onClearSelection && (
            <Button size="small" variant="text" onClick={onClearSelection} disabled={busy || ticked === 0}
              sx={{ fontSize: '0.68rem', py: 0.1, minWidth: 0, color: '#9e9e9e', textTransform: 'none' }}>
              Clear
            </Button>
          )}
        </Box>
      </Box>

      {/* ── the value fields ───────────────────────────────── */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25, mb: 1.25 }}>
        {fields.map(f => (
          <Box key={f.key} sx={{ flex: `1 1 ${f.width || 150}px`, minWidth: f.width || 150 }}>
            <Typography component="label" sx={labelSx}>{f.label}</Typography>

            {f.type === 'select' ? (
              <Select
                size="small" fullWidth displayEmpty
                value={values[f.key] ?? ''}
                onChange={e => onChange(f.key, e.target.value)}
                disabled={busy}
                sx={selectSx}
                renderValue={v => v
                  ? <Typography sx={{ fontSize: '0.78rem' }}>{v}</Typography>
                  : <Typography sx={{ fontSize: '0.78rem', color: '#b0bad0' }}>
                      {f.placeholder || 'Leave blank to keep'}
                    </Typography>}
              >
                <MenuItem value="">
                  <em style={{ color: '#9e9e9e', fontSize: '0.78rem' }}>Leave blank to keep</em>
                </MenuItem>
                {(f.options || []).map(opt => (
                  <MenuItem key={opt} value={opt}>
                    <Typography sx={{ fontSize: '0.78rem' }}>{opt}</Typography>
                  </MenuItem>
                ))}
              </Select>
            ) : (
              <TextField
                size="small" fullWidth
                type={f.type === 'money' || f.type === 'number' ? 'number' : 'text'}
                value={values[f.key] ?? ''}
                onChange={e => {
                  const v = e.target.value;
                  if ((f.type === 'money' || f.type === 'number') && v !== '' && parseFloat(v) < 0) return;
                  onChange(f.key, v);
                }}
                disabled={busy}
                placeholder={f.placeholder || 'Leave blank to keep'}
                inputProps={f.type === 'money' || f.type === 'number' ? { min: 0, step: '0.01' } : undefined}
                InputProps={f.type === 'money' ? {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#8695b7' }}>GH¢</Typography>
                    </InputAdornment>
                  ),
                } : undefined}
                sx={fieldSx}
              />
            )}
          </Box>
        ))}
      </Box>

      {/* ── actions ────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
        <Tooltip title={canApply ? `Write the filled fields to ${ticked} ${plural}` : hint}>
          <span>
            <Button
              size="small" variant="contained" onClick={onApply} disabled={!canApply}
              startIcon={applying ? <CircularProgress size={14} color="inherit" /> : <DoneAllIcon />}
              sx={{
                fontSize: '0.74rem', fontWeight: 700, textTransform: 'none', px: 2, py: 0.6,
                background: 'linear-gradient(90deg,#11998e,#38ef7d)', color: '#fff',
                boxShadow: '0 2px 8px rgba(17,153,142,0.25)',
                '&:hover': { background: 'linear-gradient(90deg,#0e8a7f,#2fd469)' },
                '&:disabled': { background: '#dfe5f2', color: '#9aa7c2', boxShadow: 'none' },
              }}>
              {applying ? 'Applying…' : `Apply to ${ticked}`}
            </Button>
          </span>
        </Tooltip>

        <Tooltip title={canDelete ? `Delete ${ticked} ${plural}` : `Tick the ${entity}s you want to delete`}>
          <span>
            <Button
              size="small" variant="outlined" onClick={onDelete} disabled={!canDelete}
              startIcon={deleting ? <CircularProgress size={14} color="inherit" /> : <DeleteIcon />}
              sx={{
                fontSize: '0.74rem', fontWeight: 700, textTransform: 'none', px: 1.75, py: 0.6,
                color: '#e53935', borderColor: 'rgba(229,57,53,0.4)', background: '#fff',
                '&:hover': { borderColor: '#e53935', background: 'rgba(229,57,53,0.06)' },
                '&:disabled': { color: '#b0bad0', borderColor: '#dfe5f2', background: '#fff' },
              }}>
              {deleting ? 'Deleting…' : `Delete ${ticked}`}
            </Button>
          </span>
        </Tooltip>

        <Typography sx={{ fontSize: '0.72rem', color: '#8695b7' }}>{hint}</Typography>
      </Box>

      {/* ── copy-one-row-to-other-schemes strip ────────────── */}
      {copy && (
        <>
          <Divider sx={{ my: 1.25, borderColor: '#dbe3f5' }} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Button
              size="small" variant="text" onClick={copy.onClick} disabled={copy.disabled || busy}
              startIcon={<CopyIcon sx={{ fontSize: 15 }} />}
              sx={{
                fontSize: '0.72rem', fontWeight: 700, textTransform: 'none',
                color: '#3949ab', py: 0.2, px: 0.75,
                '&:hover': { background: 'rgba(57,73,171,0.06)' },
                '&:disabled': { color: '#b0bad0' },
              }}>
              {copy.label}
            </Button>
            {copy.hint && (
              <Typography sx={{ fontSize: '0.7rem', color: '#9aa7c2' }}>{copy.hint}</Typography>
            )}
          </Box>
        </>
      )}
    </Box>
  );
};

export default ApplyToManyBar;
